import mpstyles from "./index.module.scss";
import { useTranslation } from "react-i18next";
import { CheckboxInput } from "components/inputs/checkbox-input";
import { GatedContentPassFormProps, PricingModalTypes } from ".";
import { Errors } from "hooks/useForm";
import { Toggle } from "components/inputs/toggle/Toggle";
import { useMemo } from "react";

export const RecurringPassField = ({
    values,
    errors,
    onChange,
    variant,
    type
}: {
    values: GatedContentPassFormProps;
    errors: Errors;
    onChange: (key: string, value: any) => void;
    loading?: boolean;
    type: PricingModalTypes;
    variant?: "catalog";
}) => {
    const { t } = useTranslation();

    const recurringPriceHasChanged = useMemo(() => {
        // Normalize current prices (strings) to cents
        const currentMonthlyPrice = Math.round(
            parseFloat(values?.recurringMonthlyPrice || "0") * 100
        );
        const currentAnnualPrice = Math.round(
            parseFloat(values?.recurringAnnualPrice || "0") * 100
        );

        // Get old prices (already in cents)
        const oldMonthlyPrice = values?.oldRecurringMonthlyPrice?.Amount || 0;
        const oldAnnualPrice = values?.oldRecurringAnnualPrice?.Amount || 0;

        // Compare prices
        return (
            currentMonthlyPrice !== oldMonthlyPrice ||
            currentAnnualPrice !== oldAnnualPrice
        );
    }, [
        values?.oldRecurringMonthlyPrice?.Amount,
        values?.oldRecurringAnnualPrice?.Amount,
        values?.recurringMonthlyPrice,
        values?.recurringAnnualPrice
    ]);

    return (
        <>
            <div
                className={`${mpstyles["pricing-form-expanded-content"]} ${
                    variant ? mpstyles[variant] : ""
                }`}
            >
                <div className={mpstyles["recurring-pass-prices-container"]}>
                    <CheckboxInput
                        checkBoxId="monthly-price-checkbox"
                        inputId="monthly-price-input"
                        inputName={
                            variant === "catalog"
                                ? t("misc:monthly-catalog-price")
                                : t("misc:monthly-price")
                        }
                        checked={values.recurringMonthlyPriceSelected}
                        onCheckboxChange={() => {
                            onChange(
                                "recurringMonthlyPriceSelected",
                                !values.recurringMonthlyPriceSelected
                            );
                        }}
                        onInputChange={(e) => {
                            onChange("recurringMonthlyPrice", e.target.value);
                        }}
                        inputType="number"
                        min={import.meta.env.VITE_MINIMUM_MONTHLY_PRICE}
                        placeholder={t("gated-content-modal:price-placeholder")}
                        value={values.recurringMonthlyPrice}
                        error={errors.recurringMonthlyPrice}
                        disabled={
                            values?.recurringMonthlyPriceSelected === false
                        }
                    />
                    <CheckboxInput
                        checkBoxId="annual-price-checkbox"
                        inputId="annual-price-input"
                        inputName={
                            variant === "catalog"
                                ? t("misc:annual-catalog-price")
                                : t("misc:annual-price")
                        }
                        checked={values.recurringAnnualPriceSelected}
                        onCheckboxChange={() =>
                            onChange(
                                "recurringAnnualPriceSelected",
                                !values.recurringAnnualPriceSelected
                            )
                        }
                        onInputChange={(e) => {
                            onChange("recurringAnnualPrice", e.target.value);
                        }}
                        inputType="number"
                        min={import.meta.env.VITE_MINIMUM_ANNUAL_PRICE}
                        placeholder={t("gated-content-modal:price-placeholder")}
                        value={values.recurringAnnualPrice}
                        error={errors.recurringAnnualPrice}
                        disabled={
                            values?.recurringAnnualPriceSelected === false
                        }
                    />
                </div>
                {type === PricingModalTypes.Update && (
                    <>
                        {recurringPriceHasChanged && (
                            <Toggle
                                label={t(
                                    "gated-content:subscription-update:update-to-new-only"
                                )}
                                on={
                                    values.recurringApplySubscriptionUpdatesNewOnly
                                }
                                onToggle={() =>
                                    onChange(
                                        "recurringApplySubscriptionUpdatesNewOnly",
                                        !values.recurringApplySubscriptionUpdatesNewOnly
                                    )
                                }
                                className={mpstyles["toggle"]}
                                reverseLayout={true}
                            />
                        )}
                        {!values.recurringApplySubscriptionUpdatesNewOnly && (
                            <div
                                className={
                                    mpstyles[
                                        "recurring-update-subscribers-warning"
                                    ]
                                }
                            >
                                {t(
                                    "gated-content:subscription-update:update-to-new-only-warning"
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};
